// Cálculos del celular del vendedor. Son REFERENCIALES: el precio, el crédito y el stock que valen los
// decide el ERP cuando llega el pedido (el frontend no calcula, docs/frontend/02). Aquí solo se muestra.
import type {
  CatalogoVendedor,
  ClienteRuta,
  LineaBorrador,
  PresentacionCat,
  ProductoCat,
} from './almacen';

export const DIAS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'] as const;
export const NOMBRE_DIA = [
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
  'Domingo',
];

/** 0 = lunes … 6 = domingo, de una fecha AAAA-MM-DD (sin zona: es el día calendario). */
export function diaDeSemana(fecha: string): number {
  const [a, m, d] = fecha.split('-').map(Number) as [number, number, number];
  return (new Date(Date.UTC(a, m - 1, d)).getUTCDay() + 6) % 7;
}

/** Hoy en Chile, AAAA-MM-DD (el celular puede tener otra zona configurada). */
export function hoyChile(ahora = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(ahora);
}

export const visitaElDia = (c: ClienteRuta, dia: number) => c.diasVisita?.[dia] === 'X';

/** La ruta del día: los clientes con ese día marcado, por su orden de visita (los sin orden al final). */
export function rutaDelDia(clientes: ClienteRuta[], dia: number): ClienteRuta[] {
  const orden = (c: ClienteRuta) => c.ordenVisita?.[dia] || Number.MAX_SAFE_INTEGER;
  return clientes
    .filter((c) => visitaElDia(c, dia))
    .sort((a, b) => orden(a) - orden(b) || a.razonSocial.localeCompare(b.razonSocial, 'es'));
}

/** Precio de lista de la presentación, con la escala que corresponda a la cantidad. */
export function precioDe(
  cat: CatalogoVendedor,
  listaId: string | null,
  presentacionId: string,
  cantidad = 1,
): number | null {
  if (!listaId) return null;
  const base = cat.precios[listaId]?.[presentacionId];
  if (base === undefined) return null;
  let valor = Number(base);
  for (const [desde, v] of cat.escalas[listaId]?.[presentacionId] ?? [])
    if (cantidad >= Number(desde)) valor = Number(v);
  return valor;
}

/** La presentación que se ofrece primero: la de venta mayorista (la de mayor factor), si tiene precio. */
export function presentacionInicial(
  p: ProductoCat,
  cat: CatalogoVendedor,
  listaId: string | null,
): PresentacionCat {
  const conPrecio = p.presentaciones.filter((x) => precioDe(cat, listaId, x.id) !== null);
  const opciones = conPrecio.length ? conPrecio : p.presentaciones;
  return [...opciones].sort((a, b) => Number(b.factor) - Number(a.factor))[0]!;
}

/** Stock disponible expresado en la presentación (cajas enteras, por ejemplo). */
export function stockEn(cat: CatalogoVendedor, productoId: string, factor: string): number {
  return Math.floor(Number(cat.stock[productoId] ?? '0') / (Number(factor) || 1) + 1e-9);
}

export function totalAprox(
  cat: CatalogoVendedor,
  listaId: string | null,
  lineas: LineaBorrador[],
): number | null {
  let t = 0;
  for (const l of lineas) {
    const p = l.precioManual
      ? Number(l.precioManual)
      : precioDe(cat, listaId, l.presentacionId, Number(l.cantidad));
    if (p === null) return null;
    t += p * Number(l.cantidad);
  }
  return Math.round(t);
}

/** Lo que pide el pedido de un producto, en unidades base (suma de sus presentaciones). */
export function pedidoBase(
  cat: CatalogoVendedor,
  lineas: LineaBorrador[],
  productoId: string,
): number {
  const p = cat.productos.find((x) => x.id === productoId);
  return lineas
    .filter((l) => l.productoId === productoId)
    .reduce(
      (s, l) =>
        s +
        Number(l.cantidad) *
          Number(p?.presentaciones.find((x) => x.id === l.presentacionId)?.factor ?? 1),
      0,
    );
}

export function pesos(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === '') return '—';
  return `$${Math.round(Number(n)).toLocaleString('es-CL')}`;
}

export const CONDICION: Record<string, string> = {
  contado: 'Contado',
  transferencia: 'Transferencia',
  cheque_al_dia: 'Cheque al día',
  cheque_a_plazo: 'Cheque a plazo',
  credito: 'Crédito',
};

export const ESTADO_PEDIDO: Record<
  string,
  { texto: string; tono: 'ok' | 'aviso' | 'malo' | 'neutro' }
> = {
  CONFIRMADA: { texto: 'Confirmado', tono: 'ok' },
  EN_APROBACION: { texto: 'Espera firma', tono: 'aviso' },
  BORRADOR: { texto: 'Revisión oficina', tono: 'aviso' },
  CERRADA_CORTA: { texto: 'Cerrado', tono: 'neutro' },
  ANULADA: { texto: 'Anulado', tono: 'malo' },
  DESCARTADA: { texto: 'Descartado', tono: 'malo' },
  SIN_STOCK: { texto: 'Sin stock', tono: 'malo' },
};

export function rutConDv(c: ClienteRuta) {
  if (!c.rut) return '';
  return `${Number(c.rut).toLocaleString('es-CL')}-${c.dv ?? ''}`;
}

export const esContado = (c: ClienteRuta) =>
  c.condicionPago === 'contado' || c.condicionPago === 'transferencia';

export const condicionTexto = (c: { condicionPago?: string; condicion?: string; plazoDias: number }) => {
  const x = c.condicion ?? c.condicionPago ?? '';
  return `${CONDICION[x] ?? x}${c.plazoDias ? ` ${c.plazoDias} días` : ''}`;
};
